import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type CourseDetail, type UnitSummary } from '../api';

const ICON: Record<string, string> = {
  content: '▤', craft: '✦', video: '▶', audio: '♪', document: '▦', webpage: '⧉',
  test: '✓', survey: '☰', assignment: '✎', ilt: '◷', section: '—',
};
const LABEL: Record<string, string> = {
  content: 'Lesson', craft: 'Interactive lesson', video: 'Video', audio: 'Audio',
  document: 'Document', webpage: 'Web page', test: 'Test', survey: 'Survey',
  assignment: 'Assignment', ilt: 'Live session',
};

export function CoursePage() {
  const { courseId } = useParams();
  const [data, setData] = useState<CourseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    api.get<CourseDetail>(`/courses/${courseId}`).then(setData).catch((e) => setError(e.message));
  }, [courseId]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const next = data.units.find((u) => u.type !== 'section' && !u.locked && u.status !== 'completed');

  return (
    <>
      <nav className="crumbs"><Link to="/">My courses</Link> <span aria-hidden>›</span> {data.course.name}</nav>
      <div className="page-head">
        <h1 className="h1">{data.course.name}</h1>
        <Link className="btn btn--ghost" to={`/courses/${data.course.id}/discussion`}>Discussion</Link>
      </div>
      {data.course.descriptionHtml && (
        <div className="prose" dangerouslySetInnerHTML={{ __html: data.course.descriptionHtml }} />
      )}

      {data.prerequisites && !data.prerequisites.met && (
        <p className="notice">
          This course opens once you have finished{' '}
          {data.prerequisites.missing.map((m, i) => (
            <span key={m.id}>
              {i > 0 && (i === data.prerequisites!.missing.length - 1 ? ' and ' : ', ')}
              <Link to={`/courses/${m.id}`}>{m.name}</Link>
            </span>
          ))}
          . Everything below stays locked until then.
        </p>
      )}

      {data.enrollment && (
        <div className="progress-strip">
          <div className="bar"><span style={{ width: `${data.enrollment.completionPct}%` }} /></div>
          <span>{data.enrollment.completionPct}% complete</span>
          {next && <Link className="btn btn--primary" to={`/courses/${courseId}/units/${next.id}`}>
            {data.enrollment.completionPct === 0 ? 'Start' : 'Continue'}
          </Link>}
        </div>
      )}

      <ol className="units">
        {data.units.map((u) => <UnitRow key={u.id} u={u} courseId={courseId!} />)}
      </ol>
    </>
  );
}

function UnitRow({ u, courseId }: { u: UnitSummary; courseId: string }) {
  if (u.type === 'section') return <li className="units__section">{u.name}</li>;

  const inner = (
    <>
      <span className={`units__icon status-${u.status}`} aria-hidden>{ICON[u.type] ?? '•'}</span>
      <span className="units__name">{u.name}</span>
      <span className="units__type muted">{LABEL[u.type] ?? u.type}</span>
      <span className={`pill pill--${u.status}`}>
        {u.locked && u.opensAt
          ? `Opens ${new Date(u.opensAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
          : u.locked ? 'Locked'
          : u.status === 'completed' ? (u.score != null ? `Passed · ${u.score}%` : 'Done')
          : u.status === 'failed' ? 'Not passed'
          : u.status === 'pending_review' ? 'Being marked'
          : u.status === 'incomplete' ? 'In progress' : ''}
      </span>
    </>
  );

  if (u.locked) return <li className="units__row is-locked" aria-disabled>{inner}</li>;
  return (
    <li className="units__row">
      <Link to={`/courses/${courseId}/units/${u.id}`}>{inner}</Link>
    </li>
  );
}
